/**
 * Centralized API endpoint configuration
 * Defines all API routes for System Tower (Platform Admin)
 */

// Service base paths
const SERVICES = {
  PORTAL: "portal-service",
  USER: "user-service",
  AUTH: "auth-service",
};

// API Endpoints for System Tower
export const API_ENDPOINTS = {
  // Authentication (for System Admin / Portal Admin)
  AUTH: {
    LOGIN_ADMIN: `${SERVICES.USER}/auth/login/admin`,
    LOGOUT: `${SERVICES.USER}/auth/logout`,
    ME: `${SERVICES.USER}/auth/me`,
    REFRESH: `${SERVICES.USER}/auth/refresh`,
    PASSWORD_SET: `${SERVICES.USER}/auth/password/set`,
    PASSWORD_CHANGE: `${SERVICES.USER}/auth/password/change/admin`,
    PASSWORD_POLICY: `${SERVICES.USER}/auth/password-policy`,
    FORGOT_PASSWORD: `${SERVICES.USER}/auth/forgot-password/admin`,
    VERIFY_RESET_TOKEN: `${SERVICES.USER}/auth/verify-reset-token/admin`,
    RESET_PASSWORD: `${SERVICES.USER}/auth/reset-password/admin`,
    MFA_SETUP: `${SERVICES.USER}/auth/mfa/setup`,
    MFA_VERIFY: `${SERVICES.USER}/auth/mfa/verify`,
    MFA_VALIDATE: `${SERVICES.USER}/auth/mfa/validate/admin`,
    MFA_DISABLE: `${SERVICES.USER}/auth/mfa/disable`,
    MFA_RECOVERY: `${SERVICES.USER}/auth/mfa/recovery`,
    MFA_STATUS: `${SERVICES.USER}/auth/mfa/status`,
    MFA_FORCED_SETUP_INIT: `${SERVICES.USER}/auth/mfa/setup-required/init/admin`,
    MFA_FORCED_SETUP_CONFIRM: `${SERVICES.USER}/auth/mfa/setup-required/confirm/admin`,
  },

  // System Admin Management
  SYSTEM: {
    // Portal Management (portal-service) - consolidated API
    PORTALS: {
      BASE: `${SERVICES.PORTAL}/portals`,
      DETAIL: (id: string) => `${SERVICES.PORTAL}/portals/${id}`,
      RETRY_PROVISIONING: (id: string) => `${SERVICES.PORTAL}/portals/${id}/retry-provisioning`,
      ANALYTICS: (id: string) => `${SERVICES.PORTAL}/portals/${id}/analytics`,
      IMPORT: {
        SOURCE_AGENTS: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/source-list/agents`,
        SOURCE_CMS_PAGES: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/source-list/cms-pages`,
        SOURCE_FILES: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/source-list/files`,
        ALREADY_COPIED: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/already-copied`,
        AGENTS: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/agents`,
        CMS_PAGES: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/cms-pages`,
        FILES: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/files`,
        LLM_MODELS: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/llm-models`,
        SOURCE_LLM_MODELS: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/source-list/llm-models`,
        HISTORY: (id: string) => `${SERVICES.PORTAL}/portals/${id}/import/history`,
      },
    },
    // Portal Custom Domains (portal-service)
    DOMAINS: {
      LIST: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/domains`,
      ADD: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/domains`,
      VERIFY: (portalId: string, domainId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/domains/${domainId}/verify`,
      REMOVE: (portalId: string, domainId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/domains/${domainId}`,
    },
    // System Admins (user-service)
    ADMINS: {
      BASE: `${SERVICES.USER}/system/admins`,
      DETAIL: (id: string) => `${SERVICES.USER}/system/admins/${id}`,
    },
    // Portal Admins (user-service)
    PORTAL_ADMINS: {
      BASE: `${SERVICES.USER}/system/portal-admins`,
      DETAIL: (id: string) => `${SERVICES.USER}/system/portal-admins/${id}`,
    },
    // Master LLM Models catalog (portal-service)
    LLM_MODELS: {
      BASE: `${SERVICES.PORTAL}/llm-models`,
    },
    // SSO Providers (portal-service)
    SSO_PROVIDERS: {
      LIST: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/sso-providers`,
      ADD: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/sso-providers`,
      UPDATE: (portalId: string, id: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/sso-providers/${id}`,
      REMOVE: (portalId: string, id: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/sso-providers/${id}`,
    },
    // Portal Roles (user-service, system-admin scoped)
    PORTAL_ROLES: {
      LIST: (portalId: string) =>
        `${SERVICES.USER}/portals/${portalId}/roles`,
    },
    // External API (portal-service)
    EXTERNAL_API: {
      CONFIG: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/external-api`,
      ROTATE: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/external-api/rotate`,
      DOCS: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/external-api/docs`,
      SCOPES: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/external-api/scopes`,
    },
    // Log Viewer / ELK (portal-service)
    LOG_VIEWER: {
      PROVISION: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/elk`,
      RESET_PASSWORD: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/elk/reset-password`,
      DEPROVISION: (portalId: string) =>
        `${SERVICES.PORTAL}/portals/${portalId}/elk`,
    },
    // Analytics (portal-service)
    ANALYTICS: `${SERVICES.PORTAL}/system/analytics`,
    // System Settings (portal-service)
    SETTINGS: `${SERVICES.PORTAL}/system/settings`,
    // System SSO (portal-service)
    SSO: `${SERVICES.PORTAL}/system/sso`,
    SSO_PORTAL_ASSIGNMENTS: `${SERVICES.PORTAL}/system/sso/portal-assignments`,
  },

  // SSO Authentication (user-service)
  SSO: {
    SYSTEM_STATUS: `${SERVICES.USER}/auth/system-sso/status`,
    SYSTEM_LOGIN: `${SERVICES.USER}/auth/system-sso/login`,
    SYSTEM_EXCHANGE: `${SERVICES.USER}/auth/system-sso/exchange`,
  },
};
